/**
 * Header Tool for EditorJS
 *
 * H1-H6 레벨을 지원하는 자체 내재화 헤더 블록 툴.
 * 백스페이스로 paragraph 변환, 커서 복원 기능 포함.
 *
 * Based on @editorjs/header (MIT) + header_with_backspace.js
 */

// --- SVG Icons (extracted from @editorjs/header) ---

const IconH1 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M19 17V10.2135C19 10.1287 18.9011 10.0824 18.836 10.1367L16 12.5"/></svg>';
const IconH2 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M16 11C16 10 19 9.5 19 12C19 13.9771 16.0684 13.9997 16.0012 16.8981C15.9999 16.9533 16.0448 17 16.1 17L19.3 17"/></svg>';
const IconH3 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M16 11C16 10.5 16.8323 10 17.6 10C18.3677 10 19.5 10.311 19.5 11.5C19.5 12.5315 18.7474 12.9022 18.548 12.9823C18.5378 12.9864 18.5395 13.0047 18.5503 13.0063C18.8115 13.0456 20 13.3065 20 14.8C20 16 19.5 17 17.8 17C17.8 17 16 17 16 16.3"/></svg>';
const IconH4 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M18 10L15.2834 14.8511C15.246 14.9178 15.294 15 15.3704 15C16.8489 15 18.7561 15 20.2 15M19 17C19 15.7187 19 14.8813 19 13.6"/></svg>';
const IconH5 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M16 15.9C16 15.9 16.3768 17 17.8 17C19.5 17 20 15.6199 20 14.7C20 12.7323 17.6745 12.0486 16.1635 12.9894C16.094 13.0327 16 12.9846 16 12.9027V10.1C16 10.0448 16.0448 10 16.1 10H19.8"/></svg>';
const IconH6 = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M6 7L6 12M6 17L6 12M6 12L12 12M12 7V12M12 17L12 12"/><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M19.5 10C16.5 10.5 16 13.3285 16 15M16 15V15C16 16.1046 16.8954 17 18 17H18.3246C19.3251 17 20.3191 16.3492 20.2522 15.3509C20.0612 12.4958 16 12.6611 16 15Z"/></svg>';
const IconHeading = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M9 7L9 12M9 17V12M9 12L15 12M15 7V12M15 17L15 12"/></svg>';

const ALL_LEVELS = [
  { number: 1, tag: 'H1', svg: IconH1 },
  { number: 2, tag: 'H2', svg: IconH2 },
  { number: 3, tag: 'H3', svg: IconH3 },
  { number: 4, tag: 'H4', svg: IconH4 },
  { number: 5, tag: 'H5', svg: IconH5 },
  { number: 6, tag: 'H6', svg: IconH6 },
];

export default class Header {
  constructor({ data, config, api, readOnly }) {
    this.api = api;
    this.readOnly = readOnly;
    this._settings = config || {};
    this._data = this._normalizeData(data);
    this._element = this._getTag();
  }

  // --- Static API ---

  static get conversionConfig() {
    return { export: 'text', import: 'text' };
  }

  static get sanitize() {
    return { level: false, text: {} };
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get pasteConfig() {
    return { tags: ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'] };
  }

  static get toolbox() {
    return { icon: IconHeading, title: 'Heading' };
  }

  // --- Instance Methods ---

  render() {
    return this._element;
  }

  renderSettings() {
    return this.levels.map((level) => ({
      icon: level.svg,
      label: this.api.i18n.t(`Heading ${level.number}`),
      onActivate: () => this.setLevel(level.number),
      closeOnActivate: true,
      isActive: this.currentLevel.number === level.number,
      render: () => document.createElement('div'),
    }));
  }

  setLevel(level) {
    this.data = { level, text: this.data.text };
  }

  save(toolsContent) {
    return {
      text: toolsContent.innerHTML,
      level: this.currentLevel.number,
    };
  }

  validate(blockData) {
    return blockData.text.trim() !== '';
  }

  merge(data) {
    this._element.insertAdjacentHTML('beforeend', data.text);
  }

  onPaste(event) {
    const detail = event.detail;
    if (!('data' in detail)) return;

    const pastedElement = detail.data;
    let level = this.defaultLevel.number;

    switch (pastedElement.tagName) {
      case 'H1': level = 1; break;
      case 'H2': level = 2; break;
      case 'H3': level = 3; break;
      case 'H4': level = 4; break;
      case 'H5': level = 5; break;
      case 'H6': level = 6; break;
    }

    // 설정된 레벨 목록이 있으면 가장 가까운 레벨로 맞춤
    if (this._settings.levels) {
      level = this._settings.levels.reduce((prev, curr) =>
        Math.abs(curr - level) < Math.abs(prev - level) ? curr : prev
      );
    }

    this.data = { level, text: pastedElement.innerHTML };
  }

  // --- Data getter/setter ---

  get data() {
    this._data.text = this._element.innerHTML;
    this._data.level = this.currentLevel.number;
    return this._data;
  }

  set data(newData) {
    this._data = this._normalizeData(newData);

    if (newData.level !== undefined) {
      this._element.dataset.level = this.currentLevel.number;
    }

    if (newData.text !== undefined) {
      this._element.innerHTML = this._data.text || '';
    }
  }

  // --- Level helpers ---

  get levels() {
    if (this._settings.levels) {
      return ALL_LEVELS.filter((l) => this._settings.levels.includes(l.number));
    }
    return ALL_LEVELS;
  }

  get currentLevel() {
    const found = this.levels.find((l) => l.number === this._data.level);
    return found || this.defaultLevel;
  }

  get defaultLevel() {
    if (this._settings.defaultLevel) {
      const found = this.levels.find((l) => l.number === this._settings.defaultLevel);
      if (found) return found;
    }
    return this.levels[0];
  }

  // --- Private ---

  _normalizeData(data) {
    const result = { text: '', level: this.defaultLevel.number };
    if (data && data.text !== undefined) {
      result.text = data.text || '';
      if (data.level !== undefined && !isNaN(parseInt(data.level))) {
        result.level = parseInt(data.level);
      }
    }
    return result;
  }

  _getTag() {
    const el = document.createElement('div');

    el.innerHTML = this._data.text || '';
    el.classList.add('ce-header');
    el.dataset.level = this.currentLevel.number;
    el.contentEditable = this.readOnly ? 'false' : 'true';
    el.dataset.placeholder = this.api.i18n.t(this._settings.placeholder || '');

    if (!this.readOnly) {
      this._attachEventListeners(el);
    }

    return el;
  }

  /**
   * 백스페이스 → paragraph 변환 + 빈 헤더 커서 보정
   */
  _attachEventListeners(element) {
    element.addEventListener('keydown', (event) => {
      if (event.key !== 'Backspace') return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      if (range.startOffset !== 0) return;

      // 커서가 맨 앞에 있을 때 → paragraph로 변환
      const currentIndex = this.api.blocks.getCurrentBlockIndex();
      const currentBlock = this.api.blocks.getBlockByIndex(currentIndex);
      if (!currentBlock || !currentBlock.id) return;

      event.preventDefault();
      event.stopPropagation();

      const text = element.textContent || '';

      this.api.blocks.convert(currentBlock.id, 'paragraph', { text })
        .then(() => {
          setTimeout(() => {
            const newIndex = this.api.blocks.getCurrentBlockIndex();
            const newBlock = this.api.blocks.getBlockByIndex(newIndex);
            if (!newBlock || !newBlock.holder) return;

            const paragraph = newBlock.holder.querySelector('.ce-paragraph');
            if (!paragraph) return;

            paragraph.focus();
            try {
              const r = document.createRange();
              const sel = window.getSelection();
              const textNode = this._getFirstTextNode(paragraph);

              if (textNode.nodeType === Node.TEXT_NODE) {
                r.setStart(textNode, 0);
              } else {
                r.setStart(paragraph, 0);
              }
              r.collapse(true);
              sel.removeAllRanges();
              sel.addRange(r);
            } catch { /* DOM 교체 시 무시 */ }
          }, 50);
        })
        .catch((err) => {
          console.error('[Header] backspace 변환 실패:', err);
        });
    }, true);

    element.addEventListener('input', () => {
      const text = element.textContent || '';
      if (text.length !== 0) return;

      setTimeout(() => {
        try {
          let targetNode = null;

          if (element.childNodes.length > 0) {
            const firstNode = element.childNodes[0];
            if (firstNode.nodeName === 'BR') {
              element.removeChild(firstNode);
              targetNode = document.createTextNode('');
              element.appendChild(targetNode);
            } else {
              targetNode = firstNode;
            }
          } else {
            targetNode = document.createTextNode('');
            element.appendChild(targetNode);
          }

          const r = document.createRange();
          const sel = window.getSelection();

          if (targetNode.nodeType === Node.TEXT_NODE) {
            r.setStart(targetNode, 0);
            r.setEnd(targetNode, 0);
          } else {
            r.setStart(element, 0);
            r.setEnd(element, 0);
          }
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        } catch (err) {
          console.error('[Header] 커서 보정 실패:', err);
        }
      }, 0);
    }, true);
  }

  _getFirstTextNode(node) {
    if (node.nodeType === Node.TEXT_NODE) return node;
    if (node.firstChild) return this._getFirstTextNode(node.firstChild);
    return node;
  }
}
